/**
 * Dashboard API client service for Creative Analytics Dashboard (Phase 28).
 * Provides type-safe API calls for all dashboard endpoints across 5 tabs.
 *
 * Base path: /teams/{teamId}/dashboard/...
 * Uses the existing `api` client from services/api.ts for auth + error handling.
 */

import type {
  DashboardFilters,
  VerdictSummary,
  GateSummary,
  KpiDriverGroup,
  CompositionData,
  CalibrationData,
  ExplorerData,
  CreativesListResponse,
  SavedReport,
  ReportConfig,
  ReportPreviewResponse,
  ReportExportError,
  ReportOptions,
  GroupField,
  ShareBasis,
} from '@/types/dashboard';
import { ReportTooLargeError } from '@/types/dashboard';

import { api } from './api';
import { fetchAuthSession } from './auth';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Convert DashboardFilters to query params for API calls.
 */
export function filtersToParams(
  filters: DashboardFilters
): Record<string, string | undefined> {
  return {
    templateId: filters.templateId,
    projectId: filters.projectId !== 'all' ? filters.projectId : undefined,
    period: filters.period,
    mediaType: filters.mediaType !== 'all' ? filters.mediaType : undefined,
    from: filters.period === 'custom' ? filters.fromDate : undefined,
    to: filters.period === 'custom' ? filters.toDate : undefined,
  };
}

// ============================================================================
// VERDICTS TAB
// ============================================================================

export async function getVerdictSummary(
  teamId: string,
  filters: DashboardFilters
): Promise<VerdictSummary> {
  return api.get<VerdictSummary>(
    `/teams/${teamId}/dashboard/verdicts`,
    { params: filtersToParams(filters) }
  );
}

// ============================================================================
// GATES TAB
// ============================================================================

export async function getGateSummary(
  teamId: string,
  filters: DashboardFilters,
  columnGroup: string = 'placement'
): Promise<GateSummary> {
  return api.get<GateSummary>(
    `/teams/${teamId}/dashboard/gates`,
    { params: { ...filtersToParams(filters), columnGroup } }
  );
}

// ============================================================================
// INSIGHTS TAB — KPI DRIVERS
// ============================================================================

export async function getKpiDrivers(
  teamId: string,
  filters: DashboardFilters,
  kpiId: string,
  scope: 'smart' | 'goal' | 'bucket' | 'all'
): Promise<KpiDriverGroup> {
  return api.get<KpiDriverGroup>(
    `/teams/${teamId}/dashboard/insights/drivers`,
    { params: { ...filtersToParams(filters), kpiId, scope } }
  );
}

// ============================================================================
// INSIGHTS TAB — COMPOSITION
// ============================================================================

export async function getComposition(
  teamId: string,
  filters: DashboardFilters,
  kpiId: string,
  groupField: GroupField,
  shareBasis: ShareBasis
): Promise<CompositionData> {
  return api.get<CompositionData>(
    `/teams/${teamId}/dashboard/insights/composition`,
    {
      params: {
        ...filtersToParams(filters),
        kpiId,
        groupFieldKind: groupField.kind,
        groupFieldId: groupField.id,
        shareBasis,
      },
    }
  );
}

// ============================================================================
// INSIGHTS TAB — CALIBRATION
// ============================================================================

export async function getCalibration(
  teamId: string,
  filters: DashboardFilters,
  xAxis?: string
): Promise<CalibrationData> {
  return api.get<CalibrationData>(
    `/teams/${teamId}/dashboard/insights/calibration`,
    { params: { ...filtersToParams(filters), xAxis } }
  );
}

// ============================================================================
// INSIGHTS TAB — EXPLORER
// ============================================================================

export async function getExplorer(
  teamId: string,
  filters: DashboardFilters,
  xAxis: string,
  yAxis: string
): Promise<ExplorerData> {
  return api.get<ExplorerData>(
    `/teams/${teamId}/dashboard/insights/explorer`,
    { params: { ...filtersToParams(filters), xAxis, yAxis } }
  );
}

// ============================================================================
// CREATIVES TAB
// ============================================================================

export async function getCreatives(
  teamId: string,
  filters: DashboardFilters,
  page: number,
  pageSize: number,
  sort: string,
  order: 'asc' | 'desc',
  gates?: string[],
  search?: string
): Promise<CreativesListResponse> {
  const gatesParam = gates?.length ? gates.join(',') : undefined;
  return api.get<CreativesListResponse>(
    `/teams/${teamId}/dashboard/creatives`,
    {
      params: {
        ...filtersToParams(filters),
        page: String(page),
        pageSize: String(pageSize),
        sort,
        order,
        gates: gatesParam,
        search,
      },
    }
  );
}

export async function getCreativesCsv(
  teamId: string,
  filters: DashboardFilters
): Promise<Blob> {
  const API_BASE_URL = import.meta.env.VITE_API_URL;
  const url = new URL(`${API_BASE_URL}/teams/${teamId}/dashboard/creatives/export`);
  const params = filtersToParams(filters);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value));
    }
  });

  const session = await fetchAuthSession();
  const token = session?.idToken;

  const headers: HeadersInit = { Accept: 'text/csv' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), { method: 'GET', headers });

  if (!response.ok) {
    throw new Error(`CSV export failed: ${response.statusText}`);
  }

  return response.blob();
}

// ============================================================================
// REPORT OPTIONS (Phase 36-02)
// ============================================================================

export async function getReportOptions(
  teamId: string,
  templateId?: string
): Promise<ReportOptions> {
  const params = templateId ? `?templateId=${templateId}` : '';
  return api.get<ReportOptions>(`/teams/${teamId}/dashboard/report-options${params}`);
}

// ============================================================================
// REPORTS TAB
// ============================================================================

export async function getReports(
  teamId: string
): Promise<SavedReport[]> {
  // 37.1-05 (D-C3-unwrap): backend returns success_response({"reports": [...]}); api.ts
  // unwraps the OUTER `data` envelope; this service unwraps the INNER `reports` field
  // so consumers receive a flat SavedReport[] matching the type annotation.
  const result = await api.get<{ reports: SavedReport[] }>(
    `/teams/${teamId}/dashboard/reports`
  );
  return result.reports;
}

export async function createReport(
  teamId: string,
  config: ReportConfig
): Promise<SavedReport> {
  // 37.1-05 (D-C3-unwrap): backend returns success_response({"report": report}, 201);
  // api.ts unwraps the OUTER `data` envelope; this service unwraps the INNER `report`
  // field so consumers receive a flat SavedReport matching the type annotation.
  // 37.1-04 UAT Test 6 evidence: without this unwrap, ReportBuilder.handleSaveThenExport
  // reads saved.reportId as undefined and emits GET /reports/undefined/export → 404.
  const result = await api.post<{ report: SavedReport }>(
    `/teams/${teamId}/dashboard/reports`,
    config
  );
  return result.report;
}

export async function updateReport(
  teamId: string,
  reportId: string,
  config: Partial<ReportConfig>
): Promise<SavedReport> {
  // 37.1-05 (D-C3-unwrap): backend returns success_response({"report": report}); api.ts
  // unwraps OUTER `data` envelope; this service unwraps INNER `report` for parity with
  // createReport so update-then-export uses the same shape contract.
  const result = await api.put<{ report: SavedReport }>(
    `/teams/${teamId}/dashboard/reports/${reportId}`,
    config
  );
  return result.report;
}

export async function deleteReport(
  teamId: string,
  reportId: string
): Promise<void> {
  return api.delete(`/teams/${teamId}/dashboard/reports/${reportId}`);
}

export async function previewReport(
  teamId: string,
  config: ReportConfig
): Promise<ReportPreviewResponse> {
  // Phase 37 Plan 05 Task 05a: api client auto-unwraps `.data` (Case A verified
  // in services/api.ts lines 180-185). We must hit a lower-level fetch here to
  // preserve the full envelope {data, meta} emitted by Plan 04's handler so the
  // Builder can surface cap/warning state (D-11, D-13, D-14).
  const API_BASE_URL = import.meta.env.VITE_API_URL;
  const url = `${API_BASE_URL}/teams/${teamId}/dashboard/reports/preview`;

  const session = await fetchAuthSession();
  const token = session?.idToken;

  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    throw new Error(`Preview failed: ${response.statusText}`);
  }

  // Expected shape: { data: ReportPreviewData, meta: ReportPreviewMeta & { requestId, timestamp } }
  const body = (await response.json()) as ReportPreviewResponse;
  return body;
}

export async function exportReportCsv(
  teamId: string,
  reportId: string
): Promise<Blob> {
  const API_BASE_URL = import.meta.env.VITE_API_URL;
  const url = new URL(`${API_BASE_URL}/teams/${teamId}/dashboard/reports/${reportId}/export`);

  const session = await fetchAuthSession();
  const token = session?.idToken;

  const headers: HeadersInit = { Accept: 'text/csv' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), { method: 'GET', headers });

  if (response.status === 413) {
    // Phase 37 Plan 04 (D-12): structured REPORT_TOO_LARGE error.
    // Server body shape: { error: { code, message, totalRows, cap }, meta: {...} }.
    // Parse and throw a typed ReportTooLargeError so UI handlers can distinguish
    // this error class and surface the server's guidance message directly.
    const body = await response.json().catch(() => null);
    const errPayload = (body && body.error) as ReportExportError | undefined;
    if (errPayload && errPayload.code === 'REPORT_TOO_LARGE') {
      throw new ReportTooLargeError(errPayload);
    }
    throw new Error(`Report too large (413): ${response.statusText}`);
  }

  if (!response.ok) {
    throw new Error(`Report CSV export failed: ${response.statusText}`);
  }

  return response.blob();
}

// ============================================================================
// HOME PAGE METRICS
// ============================================================================

export interface HomeMetrics {
  total: number;
  scale: number;
  fix: number;
  kill: number;
  awaitingAnalysis: number;
}

export async function getHomeMetrics(teamId: string): Promise<HomeMetrics> {
  return api.get<HomeMetrics>(`/teams/${teamId}/dashboard/home-metrics`);
}
